import { memo, useCallback } from 'react'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Avatar, Box, Button, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import dayjs from 'dayjs'
import { getNotificationMeta } from './notification-meta.util'
import { NOTIFICATION_CATEGORIES, NOTIFICATION_VIEW_AVAILABILITY } from './notification-center.const'

const SECONDARY_TEXT_SX = { fontSize: 12, color: 'text.secondary', wordBreak: 'break-word' }

function NotificationBody({ notification, meta }) {
  const { _ } = useLingui()
  const crashId = notification.crash_id
  const hasCrashSubtitle = Boolean(crashId) && notification.category !== NOTIFICATION_CATEGORIES.OFFLINE_IMPORT
  const description = meta.description ? _(meta.description) : notification.message

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>{_(meta.title)}</Typography>
      {hasCrashSubtitle && <Typography sx={SECONDARY_TEXT_SX}>{_(msg`Crash #${crashId}`)}</Typography>}
      <Typography sx={SECONDARY_TEXT_SX}>{description}</Typography>
    </Box>
  )
}

function NotificationViewAction({ viewAvailability, onView }) {
  if (viewAvailability === NOTIFICATION_VIEW_AVAILABILITY.DESKTOP_ONLY) {
    return <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}><Trans>Available on desktop</Trans></Typography>
  }
  if (viewAvailability !== NOTIFICATION_VIEW_AVAILABILITY.ENABLED) return null
  return (
    <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={onView} sx={{ mt: 0.5 }}>
      <Trans>View</Trans>
    </Button>
  )
}

function NotificationAside({ notification, meta, viewAvailability, onView }) {
  const { _ } = useLingui()
  const author = meta.isSystemAuthored ? _(msg`system`) : notification.username || 'admin'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
        {notification.ts ? dayjs(notification.ts).format('DD/MM/YYYY HH:mm') : ''}
      </Typography>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <PersonOutlineIcon sx={{ fontSize: 14 }} />{author}
      </Typography>
      <NotificationViewAction viewAvailability={viewAvailability} onView={onView} />
    </Box>
  )
}

/**
 * One Notification Center row. Both actions mark it read: clicking the row selects it (crash preview on the map),
 * "View" opens its target (crash form, users list, imports).
 * @param {{ notification: object, viewAvailability: string, onSelect: (notification: object) => void, onView: (notification: object) => void }} props
 */
export const NotificationListItem = memo(function NotificationListItem({ notification, viewAvailability, onSelect, onView }) {
  const theme = useTheme()
  const { _ } = useLingui()
  const meta = getNotificationMeta(notification)
  const isUnread = !notification.status
  const accent = theme.palette[meta.color]?.main ?? theme.palette.primary.main

  const handleSelect = useCallback(() => {
    onSelect(notification)
  }, [onSelect, notification])

  const handleView = useCallback(event => {
    event.stopPropagation()
    onView(notification)
  }, [onView, notification])

  return (
    <Box
      onClick={handleSelect}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5, mb: 1, borderRadius: 2, cursor: 'pointer',
        border: `1px solid ${isUnread ? alpha(theme.palette.info.main, 0.5) : theme.palette.divider}`,
        bgcolor: isUnread ? alpha(theme.palette.info.main, 0.08) : 'background.paper',
        transition: theme.transitions.create(['background-color', 'box-shadow'], { duration: theme.transitions.duration.shorter }),
        '&:hover': { boxShadow: theme.shadows[2] },
      }}
    >
      <Avatar variant="rounded" sx={{ bgcolor: alpha(accent, 0.15), color: accent, width: 40, height: 40 }}>
        <meta.Icon fontSize="small" />
      </Avatar>
      <NotificationBody notification={notification} meta={meta} />
      <NotificationAside notification={notification} meta={meta} viewAvailability={viewAvailability} onView={handleView} />
      {isUnread && (
        <Box aria-label={_(msg`Unread`)} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'info.main', alignSelf: 'center', flexShrink: 0 }} />
      )}
    </Box>
  )
})
